import { useCallback, useMemo } from 'react';
import { SsoService, TenantSlugDiscovery } from 'api';
import { FIRM_WATERFALL } from 'common/constants/user';
import { WATERFALL_SLUG } from 'common/constants/waterfall';
import { Logger } from 'utilities';

// Waterfall page paths that require tenant discovery before deciding whether to redirect to SSO:
// https://host/firms/firm-slug/companies/company-slug/waterfall?cap-table-version=cap-table-19833&date=2025-09-30&exit-date=2026-03-03
// https://host/firms/firm-slug/firm-waterfall?cap-table-version=cap-table-19833&date=2025-09-30&exit-date=2026-03-03
const WATERFALL_REGEX = new RegExp(String.raw`^/firms/[\w-]+/(companies/[\w-]+/${WATERFALL_SLUG}|${FIRM_WATERFALL})$`);

const TENANT_DISCOVERY_ENTITY_MAP = {
  companies: TenantSlugDiscovery.entity.COMPANY,
  firms: TenantSlugDiscovery.entity.FIRM,
} as const;

const useTenantDiscovery = ({ location }: { location: string }) => {
  const needsDiscovery = useMemo(() => {
    const url = new URL(location);
    return WATERFALL_REGEX.test(url.pathname);
  }, [location]);

  const { entity, slug } = useMemo(() => {
    const url = new URL(location);
    const pathSegments = url.pathname.split('/').filter(Boolean);

    // Waterfall URLs start with /{entity}/{slug}/..., e.g. /firms/acme/...
    return {
      entity: pathSegments[0] ?? '',
      slug: pathSegments[1] ?? '',
    };
  }, [location]);

  const discoverTenantBySlug = useCallback(async () => {
    const requestEntity = TENANT_DISCOVERY_ENTITY_MAP[entity as keyof typeof TENANT_DISCOVERY_ENTITY_MAP];
    if (!slug || !requestEntity) {
      Logger.error('Invalid URL structure for tenant discovery. Slug or entity is missing or unrecognized.', {
        entity,
        slug,
      });
      return undefined;
    }

    try {
      const response = await SsoService.apiSsoSlugDiscoveryCreate({
        entity: requestEntity,
        redirect_to: location,
        slug,
      });
      return response;
    } catch (error) {
      Logger.error('Error discovering tenant by slug for SSO.', error);
      return undefined;
    }
  }, [entity, location, slug]);

  return { discoverTenantBySlug, needsDiscovery };
};

export default useTenantDiscovery;
